// =============================================================================
// Checklist dual-run glue (Phase 3) — the thin layer the FOH 67 screen calls so a
// checklist tick (and a Random Act of Kindness) writes to BOTH the old Apps Script
// sheet (unchanged, still the source of truth) AND the new Supabase tables, side by
// side. Sibling of stockSync.js / selloutSync.js and the SAME contract:
//
//   * BEST-EFFORT. Every recorder swallows its own errors and can NEVER throw into
//     the live till path. A Supabase hiccup silently skips the shadow; the sheet
//     write is untouched and the gap shows up in a reconcile, never on the till.
//   * PER-ACTION PIN. Each tick re-verifies the PIN the staff just entered
//     (ensurePass), which is what makes the who-did-it stamp truthful — the pass
//     minted carries THIS person's staff id and the database reads it off the token
//     (never a value we pass up). A slow/failed login just skips the shadow.
//   * GATED. Runs only where stockSync's dual-run gate is on (the four live kiosks,
//     or ?dualrun=on). Any other host stays completely inert.
//
// It writes through the 0440 doors (checklist_tick_record_app / checklist_rak_record),
// which check the pass in code and sign the row from the token — the browser role
// has no rights on checklist_ticks / checklist_random_acts at all.
//
// ⚠️ THIS IS THE WRITE HALF ONLY. It does not touch the checklist READ, which stays
// on the sheet (see checklist.js: CHECKLIST_READ_HOSTS empty AND
// CHECKLIST_WRITES_MIGRATED false). Flipping the read is a later, separate step.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { dualRunEnabled, siteIdForKey, shadow, idem, ensurePass } from "./stockSync.js";
import { record as recordActivity } from "./activity.js";

// Fresh at CALL time, never from React state — the day-rollover lesson: a kiosk
// that slept through midnight must stamp the new day (used only for the idem key;
// the row's own occurred_at/event_date are stamped server-side by the door).
const londonToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });

// --- activity log line ---------------------------------------------------------
// A checklist tick is also a "who did what, where, when" event for the estate
// activity feed (activity.js → activity_record). This is the exact call site
// activity.js's own footer flagged as the one to wire. Fire-and-forget, no PIN (the
// database resolves the staff id from the login pass already warm on `supabase`
// after ensurePass), and it can never break a tick. Fires only after the shadow
// tick landed, so the audit line carries the same person and the same success path.
export const _activitySink = { record: recordActivity };

export function logChecklistActivity(siteKey, task) {
  try {
    return _activitySink.record({ siteKey, action: "checklistTick", kind: "action", target: task || null, client: supabase });
  } catch (e) {
    console.warn("[checklist-sync] activity line threw (tick write unaffected):", e?.message || e);
    return null;
  }
}

// --- best-effort preamble: gate → per-action PIN pass → best-effort write -------
async function shadowWrite(label, { staffPin, siteKey }, fn) {
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  try {
    const pass = await ensurePass(staffPin, siteKey);
    if (!pass?.ok) { console.warn(`[checklist-sync] ${label} — no pass, shadow skipped`); return { ok: false, error: "no pass" }; }
    // The site-id cache warm-up doubles as an early "is Supabase reachable / does
    // this shop exist" check; the doors resolve the shop themselves from the key.
    const siteId = await siteIdForKey(siteKey);
    if (!siteId) return { ok: false, error: "no site id for " + siteKey };
    return await shadow(label, fn);
  } catch (e) {
    console.warn(`[checklist-sync] ${label} — shadow threw:`, e?.message || e);
    return { ok: false, error: e };
  }
}

// --- one checklist tick ---------------------------------------------------------
// Idem key is (site, tick, tier, section, task, day): a checklist slot is ticked
// once per sweep per day and then locks green, so a double-tap or a retried write
// collapses to the SAME one row (the door's idem_key is UNIQUE). occurred_at is
// left to the door (now(), the tick's real instant); event_date is that instant's
// London day, so a 00:30 tick files under the new day.
export async function recordChecklistTick({ siteKey, tier, section, task, staffPin, day = londonToday() }) {
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  const tierOrder = tier?.order ?? null;
  const tierName = tier?.name ?? null;
  // Full argument set (explicit nulls) so PostgREST resolves the one signature
  // unambiguously rather than leaning on named-subset defaulting. p_role/p_notes/
  // p_occurred_at are null: the door fills role from the pass and stamps now().
  const res = await shadowWrite(`tick ${section}/${task}`, { staffPin, siteKey }, () =>
    supabase.rpc("checklist_tick_record_app", {
      p_site: siteKey,
      p_tier_order: tierOrder,
      p_tier_name: tierName,
      p_section: section,
      p_task: task,
      p_role: null,
      p_notes: null,
      p_occurred_at: null,
      p_idem_key: idem(siteKey, "checklist_tick", tierOrder, section, task, day),
    }));
  // Log the audit line ONLY when a NEW row actually landed ('inserted'), not merely
  // on transport success. `shadow`/res.ok is true whenever the RPC round-trips
  // without a PostgREST error — but the door returns its real outcome INSIDE the
  // jsonb (inserted / exists / no_pass / no_site / error), all as HTTP 200. Gating
  // on res.ok alone would (a) log a phantom line when the door swallowed an insert
  // failure ('error') and (b) log a SECOND line on a retry/re-tap that idem-
  // collapsed ('exists'). 'inserted' fires exactly once per real tick — which is
  // what "fires only after the shadow tick landed" above actually means.
  if (res.ok && res.data?.status === "inserted") logChecklistActivity(siteKey, task);
  return res;
}

// --- one self-reported customer review (Lane BD) --------------------------------
// The checklist screen's third write. Until now it posted ONLY to the Google Sheet
// (foh.jsx manualReviewLog) — it is the one checklist write with a Supabase table
// (checklist_manual_reviews, 0051) but no door, so it could never leave the Sheet.
// 1060 is its door; these are its recorders, twins of the tick's:
//   * recordChecklistManualReview          — the fire-and-forget SHADOW (dual-write),
//     added on every dual-run host from merge day, exactly as the tick has since 19 Aug;
//   * recordChecklistManualReviewAuthoritative — the AWAITED, result-driven twin, used
//     on a host flipped WRITE-authoritative (checklist.js checklistWriteAuthoritative,
//     Lane BD Wave 1). Self-contained on purpose so this PR compiles + merges
//     independently of Wave 1 (#316).
//
// Idem key = (site, "checklist_review", day, fingerprint(mention+notes)). A review has
// no natural unique key (unlike a tick's slot), so we fingerprint its CONTENT: an exact
// double-submit collapses to one row; two genuinely different reviews the same day/shop
// each keep their own. The rating is NOT in the fingerprint (the kiosk always sends 5,
// and the door clamps it), so a re-submit that only differs in a stray rating still
// collapses.
//
// FNV-1a (32-bit) over a LENGTH-PREFIXED encoding of (mention, notes) -> 8 hex chars.
// The prefix "<len(mention)>|mention+notes" is injective — ("ab","c") and ("a","bc")
// can never share an input. Not crypto: a 32-bit HASH collision (two DIFFERENT reviews,
// same shop, same day, same hash) is astronomically unlikely at review volume, and the
// collapse-direction failure only ever loses a duplicate shadow — the Sheet keeps both
// while the Sheet is still authoritative.
export function reviewFingerprint(mention, notes) {
  const m = String(mention || "").trim();
  const n = String(notes || "").trim();
  const s = m.length + "|" + m + n;         // injective in (m, n)
  let h = 0x811c9dc5;                        // FNV offset basis
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;     // * FNV prime, mod 2^32
  }
  return h.toString(16).padStart(8, "0");
}

// The exact RPC args both recorders send, so the shadow and the authoritative record of
// the SAME review collapse to one row (identical idem key).
function reviewRpcArgs({ siteKey, customerMention, notes, day }) {
  const mention = String(customerMention || "").trim();
  const noteStr = String(notes || "").trim();
  return {
    p_site: siteKey,
    p_customer_mention: mention,
    p_notes: noteStr,
    p_rating: 5,                            // the kiosk always sends 5; the door clamps 1–5
    p_occurred_at: null,
    p_idem_key: idem(siteKey, "checklist_review", day, reviewFingerprint(mention, noteStr)),
  };
}

// Fire-and-forget SHADOW — gated by dualRunEnabled, never awaited, never throws.
export async function recordChecklistManualReview({ siteKey, customerMention, notes, staffPin, day = londonToday() }) {
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  return shadowWrite(`review ${siteKey}`, { staffPin, siteKey }, () =>
    supabase.rpc("checklist_manual_review_record", reviewRpcArgs({ siteKey, customerMention, notes, day })));
}

// AWAITED, result-driven twin. Returns { ok, status, errMsg?, data? }. Self-contained
// (does not lean on Wave 1's helpers) so this PR is independent of #316. Verifies the
// PIN pass (the who-did-it stamp) and the shop, does NOT short-circuit on
// dualRunEnabled (the write-authoritative gate already decided this host records to
// Supabase), and maps the 1060 door's status: inserted/exists -> ok; anything else,
// a transport error, a null result or a throw -> not ok. Never throws.
export async function recordChecklistManualReviewAuthoritative(
  { siteKey, customerMention, notes, staffPin, day = londonToday() },
  { rpc = (n, a) => supabase.rpc(n, a), pass = ensurePass, siteId = siteIdForKey } = {}
) {
  let passRes;
  try { passRes = await pass(staffPin, siteKey); } catch (e) { return { ok: false, status: "no_pass", errMsg: "your PIN wasn't accepted" }; }
  if (!passRes?.ok) return { ok: false, status: "no_pass", errMsg: "your PIN wasn't accepted" };
  let sid = null;
  try { sid = await siteId(siteKey); } catch (e) { sid = null; }
  if (!sid) return { ok: false, status: "no_site", errMsg: "this shop isn't set up for live checklist writes yet" };
  let res;
  try {
    res = await rpc("checklist_manual_review_record", reviewRpcArgs({ siteKey, customerMention, notes, day }));
  } catch (e) {
    return { ok: false, status: "error", errMsg: (e && e.message) || "the review didn't confirm" };
  }
  if (!res) return { ok: false, status: "error", errMsg: "the review didn't confirm" };
  if (res.error) return { ok: false, status: "error", errMsg: res.error.message || "the review didn't confirm" };
  const status = res.data && res.data.status != null ? String(res.data.status) : null;
  if (status === "inserted" || status === "exists") return { ok: true, status, data: res.data };
  const msg = status === "no_pass" ? "your PIN wasn't accepted"
            : status === "no_site" ? "this shop isn't set up for live checklist writes yet"
            : "the review didn't confirm";
  return { ok: false, status: status || "error", errMsg: msg, data: res.data };
}

// --- one Random Act of Kindness -------------------------------------------------
// One a day per shop is the norm (the screen shows only the latest), but the sheet
// tab is append-only, so we key on (site, rak, day, reason): a double-submit of the
// same reason collapses; a genuinely different reason gets its own row.
export async function recordChecklistRak({ siteKey, reason, staffPin, day = londonToday() }) {
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  const r = String(reason || "").trim();
  if (!r) return { ok: false, error: "empty reason" };
  return shadowWrite(`rak ${siteKey}`, { staffPin, siteKey }, () =>
    supabase.rpc("checklist_rak_record", {
      p_site: siteKey,
      p_reason: r,
      p_occurred_at: null,
      p_idem_key: idem(siteKey, "checklist_rak", day, r),
      p_staff_name: null,   // ignored on the kiosk path; the pass signs the row
    }));
}

// =============================================================================
// AUTHORITATIVE recorders (Lane BD) — the write-drop half of the checklist area.
//
// The two recorders above are the fire-and-forget SHADOW: best-effort, gated by
// dualRunEnabled, never awaited — they exist so a tick ALSO lands in Supabase while
// the Google Sheet stays the record. These are their AWAITED, RESULT-DRIVEN twins,
// used on a host that has been flipped WRITE-authoritative (checklist.js
// checklistWriteAuthoritative). Same 0440 doors, same idem keys — but the caller
// WAITS for the door's verdict and drives the honest tick/rollback UI off it, and
// the Sheet POST is NOT made. The 0440 doors always answer HTTP 200 with a jsonb
// {status}; only 'inserted'/'exists' mean the write is safely on record.
//
// The shadow recorders above are left UNTOUCHED (they remain the OFF path). These
// twins reuse the SAME ensurePass + site-id preamble, so the who-did-it stamp is
// identical, and send byte-for-byte the same RPC args + idem key, so a tick that
// dual-wrote on the OFF path and the same tick on the ON path collapse to one row.
// Injectable seams (rpc/pass/siteId) mirror checklist.js's readers so both twins are
// unit-tested with no network.
// =============================================================================

// A door status that ISN'T a success, mapped to a plain-English toast reason.
const _DOOR_MSG = {
  no_pass: "your PIN wasn't accepted",
  no_site: "this shop isn't set up for live checklist writes yet",
  error: "the write didn't confirm",
};

// Interpret an awaited supabase.rpc() result ({data,error}) from a 0440 door.
// inserted/exists -> ok; a transport error, a null result, or any other door status
// -> NOT ok, with the real status carried through so the caller can tell a definite
// no_pass/no_site (roll back) from an uncertain 'error' (the caller may re-read).
// Never throws.
function doorVerdict(res) {
  if (!res) return { ok: false, status: "error", errMsg: _DOOR_MSG.error };
  if (res.error) return { ok: false, status: "error", errMsg: res.error.message || _DOOR_MSG.error };
  const status = res.data && res.data.status != null ? String(res.data.status) : null;
  if (status === "inserted" || status === "exists") return { ok: true, status, data: res.data };
  return { ok: false, status: status || "error", errMsg: _DOOR_MSG[status] || _DOOR_MSG.error, data: res.data };
}

// The awaited preamble: verify the PIN pass (the who-did-it stamp) and confirm the
// shop resolves — the same two checks shadowWrite does, but returning a NON-ok
// verdict the caller acts on rather than silently skipping. Unlike shadowWrite it
// does NOT short-circuit on dualRunEnabled(): the write-authoritative gate has
// already decided this host records to Supabase, so a missing warm pass must surface
// as no_pass (the caller rolls the tick back and keeps the entry), never as a
// phantom success. Never throws.
async function authPreamble({ staffPin, siteKey }, pass, siteId) {
  let passRes;
  try { passRes = await pass(staffPin, siteKey); } catch (e) { return { ok: false, status: "no_pass", errMsg: _DOOR_MSG.no_pass }; }
  if (!passRes?.ok) return { ok: false, status: "no_pass", errMsg: _DOOR_MSG.no_pass };
  let sid = null;
  try { sid = await siteId(siteKey); } catch (e) { sid = null; }
  if (!sid) return { ok: false, status: "no_site", errMsg: _DOOR_MSG.no_site };
  return { ok: true };
}

// One checklist tick, AWAITED and result-driven. Returns { ok, status, errMsg?, data? }.
export async function recordChecklistTickAuthoritative(
  { siteKey, tier, section, task, staffPin, day = londonToday() },
  { rpc = (n, a) => supabase.rpc(n, a), pass = ensurePass, siteId = siteIdForKey } = {}
) {
  const tierOrder = tier?.order ?? null;
  const tierName = tier?.name ?? null;
  const pre = await authPreamble({ staffPin, siteKey }, pass, siteId);
  if (!pre.ok) return pre;
  let res;
  try {
    // MUST match recordChecklistTick's RPC args + idem key exactly (checklistSync.js
    // recordChecklistTick above), so the OFF-path shadow and the ON-path record of
    // the SAME tick collapse to one row. Pinned by a test.
    res = await rpc("checklist_tick_record_app", {
      p_site: siteKey,
      p_tier_order: tierOrder,
      p_tier_name: tierName,
      p_section: section,
      p_task: task,
      p_role: null,
      p_notes: null,
      p_occurred_at: null,
      p_idem_key: idem(siteKey, "checklist_tick", tierOrder, section, task, day),
    });
  } catch (e) {
    return { ok: false, status: "error", errMsg: (e && e.message) || _DOOR_MSG.error };
  }
  const verdict = doorVerdict(res);
  // The audit line fires exactly once, only on a genuinely NEW row — the identical
  // rule the shadow recorder uses (never on 'exists', never on a swallowed 'error').
  if (verdict.ok && verdict.status === "inserted") logChecklistActivity(siteKey, task);
  return verdict;
}

// One Random Act of Kindness, AWAITED and result-driven. No activity line — a RAK
// isn't an activity-feed event (matches the shadow recorder, which also emits none).
export async function recordChecklistRakAuthoritative(
  { siteKey, reason, staffPin, day = londonToday() },
  { rpc = (n, a) => supabase.rpc(n, a), pass = ensurePass, siteId = siteIdForKey } = {}
) {
  const r = String(reason || "").trim();
  if (!r) return { ok: false, status: "error", errMsg: "empty reason" };
  const pre = await authPreamble({ staffPin, siteKey }, pass, siteId);
  if (!pre.ok) return pre;
  let res;
  try {
    // MUST match recordChecklistRak's RPC args + idem key exactly.
    res = await rpc("checklist_rak_record", {
      p_site: siteKey,
      p_reason: r,
      p_occurred_at: null,
      p_idem_key: idem(siteKey, "checklist_rak", day, r),
      p_staff_name: null,
    });
  } catch (e) {
    return { ok: false, status: "error", errMsg: (e && e.message) || _DOOR_MSG.error };
  }
  return doorVerdict(res);
}
